import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, fmtMoney, vehicleLabel } from "@/lib/format";
import { toast } from "sonner";
import {
  ArrowLeft,
  PencilSimple,
  Trash,
  MapPin,
  EnvelopeSimple,
  Phone,
  Calendar,
  IdentificationCard,
  User,
} from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import CustomerAnalytics from "@/components/CustomerAnalytics";
import NotesTimeline from "@/components/NotesTimeline";
import DocumentList from "@/components/DocumentList";
import OutreachButtons from "@/components/OutreachButtons";
import CustomerFormDrawer from "@/components/CustomerFormDrawer";
import BookingsSection from "@/components/BookingsSection";
import { useAuth } from "@/context/AuthContext";
import { relationLabel, customerPin } from "@/lib/india";
import CustomerPinGate from "@/components/CustomerPinGate";
import { devError } from "@/lib/log";

function InfoCell({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-canvas-line last:border-b-0">
      <Icon size={14} weight="bold" className="text-ink-muted mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-0.5">
          {label}
        </div>
        <div className={`text-sm text-ink break-words ${mono ? "font-mono" : ""}`}>
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

function SalesHistory({ sales }) {
  return (
    <div className="bg-canvas-card border border-canvas-line">
      <div className="px-5 py-4 border-b border-canvas-line">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
          Purchase History
        </div>
        <h3 className="font-display text-lg font-semibold text-ink">
          {sales.length} deal{sales.length === 1 ? "" : "s"} closed
        </h3>
      </div>
      <ul className="divide-y divide-canvas-line">
        {sales.length === 0 && (
          <li className="px-5 py-8 font-mono text-xs text-ink-subtle text-center">
            No vehicles purchased yet.
          </li>
        )}
        {sales.map((s) => {
          const label = vehicleLabel(s.vehicle) || "—";
          return (
            <li
              key={s.id}
              data-testid={`history-sale-${s.id}`}
              className="px-5 py-3 flex items-center justify-between gap-3"
            >
              <div>
                <div className="text-sm font-semibold text-ink">{label}</div>
                <div className="font-mono text-[11px] text-ink-muted">
                  {fmtDate(s.sale_date)} · {s.payment_method}
                </div>
              </div>
              <div className="font-mono text-sm font-semibold text-ink">
                {fmtMoney(s.sale_price)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: full } = await api.get(`/customers/${id}`);
      setData(full);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/customers");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete customer ${data.customer.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      navigate("/customers");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onOutreachLogged = async ({ type, content }) => {
    try {
      await api.post(`/customers/${id}/notes`, { type, content });
      load();
    } catch (err) {
      devError("Failed to log outreach:", err);
    }
  };

  const saveDocuments = async (documents) => {
    try {
      const c = data.customer;
      // Spread all known fields so KYC + structured address survive the round-trip.
      const payload = { ...c, documents };
      delete payload.id;
      delete payload.created_at;
      await api.put(`/customers/${id}`, payload);
      toast.success("Documents updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!data) {
    return <div className="p-8 font-mono text-sm text-ink-muted">Loading customer…</div>;
  }

  const { customer, sales, analytics } = data;
  const bookings = data.bookings || [];
  const isAdmin = user?.role === "admin";

  return (
    <div>
      <PageHeader
        eyebrow={
          <Link to="/customers" className="hover:underline flex items-center gap-1">
            <ArrowLeft size={11} weight="bold" /> 03 / Customers
          </Link>
        }
        title={customer.name}
        subtitle={
          <span className="flex items-center gap-3 mt-2 flex-wrap">
            {customer.status === "won" ? (
              <span
                data-testid="customer-detail-status-inactive"
                className="px-2 py-0.5 border border-canvas-line bg-canvas-panel text-ink-subtle text-[11px] font-mono uppercase tracking-wider"
              >
                Inactive · Sale completed
              </span>
            ) : (
              <span
                data-testid="customer-detail-status-active"
                className="px-2 py-0.5 border border-[#00B341] bg-[#00B341]/10 text-[#00B341] text-[11px] font-mono uppercase tracking-wider"
              >
                Active
              </span>
            )}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <OutreachButtons customer={customer} onLogged={onOutreachLogged} />
            <button
              onClick={() => setEditing(true)}
              data-testid="edit-detail-button"
              className="px-3 py-2 border border-canvas-line text-sm hover:border-ink flex items-center gap-1.5"
            >
              <PencilSimple size={13} weight="bold" />
              Edit
            </button>
            {isAdmin && (
              <button
                onClick={handleDelete}
                data-testid="delete-detail-button"
                className="px-3 py-2 border border-canvas-line text-sm hover:border-destructive hover:text-destructive flex items-center gap-1.5"
              >
                <Trash size={13} weight="bold" />
                Delete
              </button>
            )}
          </div>
        }
      />

      <div className="px-8 py-6 space-y-6">
        <CustomerAnalytics analytics={analytics} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-canvas-card border border-canvas-line p-5" data-testid="customer-identity-card">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-3">
                Identity
              </div>
              <InfoCell
                icon={User}
                label="Relation"
                value={
                  customer.relation_type
                    ? `${relationLabel(customer.relation_type)} ${customer.relation_name || ""}`.trim()
                    : null
                }
              />
              <InfoCell
                icon={User}
                label="Gender"
                value={customer.gender ? customer.gender.charAt(0).toUpperCase() + customer.gender.slice(1) : null}
              />
              <InfoCell
                icon={Calendar}
                label="Date of Birth"
                value={customer.dob ? fmtDate(customer.dob) : null}
              />
            </div>

            <div className="bg-canvas-card border border-canvas-line p-5">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-3">
                Contact
              </div>
              <InfoCell icon={EnvelopeSimple} label="Email" value={customer.email} mono />
              <InfoCell icon={Phone} label="Mobile" value={customer.phone} mono />
              <InfoCell icon={Phone} label="Alternate Mobile" value={customer.alternate_phone} mono />
              <InfoCell icon={MapPin} label="Address" value={customer.address} />
              <InfoCell icon={MapPin} label="PIN Code" value={customerPin(customer)} mono />
              {!customerPin(customer) && (
                <div className="mt-3">
                  <CustomerPinGate customerId={customer.id} onSaved={load} />
                </div>
              )}
            </div>

            <div className="bg-canvas-card border border-canvas-line p-5" data-testid="customer-kyc-card">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-3">
                KYC Identifiers
              </div>
              <InfoCell icon={IdentificationCard} label="Aadhar No" value={customer.aadhar_no} mono />
              <InfoCell icon={IdentificationCard} label="PAN No" value={customer.pan_no} mono />
              <InfoCell icon={IdentificationCard} label="Driving Licence" value={customer.driving_licence_no} mono />
            </div>

            {customer.notes && (
              <div className="bg-canvas-card border border-canvas-line p-5">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-2">
                  General Notes
                </div>
                <p className="text-sm text-ink whitespace-pre-wrap">{customer.notes}</p>
              </div>
            )}

            <div className="bg-canvas-card border border-canvas-line p-5">
              <DocumentList
                documents={customer.documents || []}
                onChange={saveDocuments}
              />
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <NotesTimeline customerId={id} />
            <BookingsSection
              customerId={id}
              customer={customer}
              customerStatus={customer.status}
              bookings={bookings}
              onChanged={load}
            />
            <SalesHistory sales={sales} />
          </div>
        </div>
      </div>

      <CustomerFormDrawer
        open={editing}
        editing={customer}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          load();
        }}
      />
    </div>
  );
}
